import React, { useContext, useState } from "react";
import mixpanel from "mixpanel-browser";
import { LocalLogicTracking } from "~/tracking/utils/classes/Methods";

import type { MapProviderNames } from "~/types/options";

import { getDeviceId, getSessionId } from "~/tracking/utils/user";

export type TrackingPipelineSettings = {
  apiKey: string;
};

type EventDataProps = {
  eventType: string;
  eventAttributes?: {
    label: string;
    data: boolean | string | number | undefined;
  }[];
};

export type AnalyticsSettings = {
  key: string;
  tracker: "mixpanel";
  // Whether or not we should send the event
  isDebugMode: boolean;
  // Whether or not we should print the event
  printDebugMode: boolean;
};

type AnalyticsEventDataProps = {
  eventName: string;
  eventAttributes?: { [key: string]: boolean | string | number | undefined };
};

type TrackingContext = {
  postEvent: ({ eventType, eventAttributes }: EventDataProps) => void;
  trackAnalyticsEvent: ({
    eventName,
    eventAttributes,
  }: AnalyticsEventDataProps) => void;
};

const trackingContext = React.createContext<TrackingContext>(
  {} as TrackingContext,
);
const useTracking = () => useContext(trackingContext);

/**
 * This function has to be called once and first before trying to send analytics events
 *
 * @param projectToken Project token
 */
function initializeAnalytics(projectToken: string) {
  try {
    mixpanel.init(projectToken, {
      track_pageview: false,
      persistence: "localStorage",
    });
  } catch (_error) {
    // Do nothing. This is a best effort function
  }
}

const TrackingProvider = ({
  children,
  trackingPipelineSettings,
  analyticsSettings,
  attributes = {},
  meta,
  flags,
}: {
  children: React.ReactNode;
  trackingPipelineSettings?: TrackingPipelineSettings;
  analyticsSettings?: AnalyticsSettings;
  attributes?: {
    location?: {
      lat?: number;
      lng?: number;
    };
    locale?: "en" | "fr";
    host_url?: string;
    isMobile?: boolean;
    mapProvider?: MapProviderNames;
    host?: {
      hash?: string;
      host?: string;
      hostname?: string;
      href?: string;
      origin?: string;
      pathname?: string;
      port?: string;
      protocol?: string;
      search?: string;
    };
  };
  meta: {
    productName?: string;
    productVersion?: string;
    clientId?: string;
  };
  flags?: {
    [key: string]: boolean;
  };
}) => {
  const tracking = LocalLogicTracking(trackingPipelineSettings?.apiKey ?? "");
  const [defaultAnalyticsAttributes] = useState({
    ll_product_name: meta.productName,
    ll_product_version: meta.productVersion,
    ll_client_id: meta.clientId,
    ll_locale: attributes.locale,
    ll_location_lat: attributes.location?.lat,
    ll_location_lng: attributes.location?.lng,
  });

  /**
   * Posts an event to our own Tracking API
   *
   * @param eventType The event type
   * @param eventAttributes Extra data to be tracked with the event
   */
  const postEvent = ({ eventType, eventAttributes }: EventDataProps) => {
    const metaValues: {
      product_version: string;
      product_name: string;
      client_id?: string;
      device_id?: string;
      session_id?: string;
    } = {
      product_version: meta.productVersion || "not_defined",
      product_name: meta.productName || "",
      client_id: meta.clientId,
      device_id: getDeviceId(),
      session_id: getSessionId(),
    };

    const eventBody = {
      event_type: eventType,
      event_version: "1.0.0",
      meta: metaValues,
      attributes: {
        event_attributes: eventAttributes,
        ...attributes,
      },
    };

    if (flags?.isDebugMode) {
      console.info("Tracking event:", eventBody);
      return;
    }

    tracking.postEvent(eventBody);
  };

  /**
   * Tracks an event with a third party analytics service (Mixpanel, GA4, etc).
   * This is currently an experimental feature.
   *
   * @param eventType The event type
   * @param eventAttributes Extra data to be tracked with the event
   */
  const trackAnalyticsEvent = ({
    eventName,
    eventAttributes = {},
  }: AnalyticsEventDataProps) => {
    if (
      typeof analyticsSettings === "undefined" ||
      analyticsSettings.tracker !== "mixpanel"
    ) {
      return;
    }

    // Mixpanel debug mode still sends mixpanel events. There's no reason to really use it
    if (analyticsSettings.printDebugMode) {
      console.info("Mixpanel Debug:", {
        event: eventName,
        projectToken: analyticsSettings.key,
        properties: {
          ...eventAttributes,
          ...defaultAnalyticsAttributes,
        },
      });
    }

    if (analyticsSettings.isDebugMode) {
      return;
    }

    try {
      mixpanel.track(eventName, {
        ...eventAttributes,
        ...defaultAnalyticsAttributes,
      });
    } catch (_error) {
      // Do nothing
      // Sometimes the init function takes longer than expected
      // This makes the first tracking call fail. We're ok with that.
    }
  };

  return (
    <trackingContext.Provider value={{ postEvent, trackAnalyticsEvent }}>
      {children}
    </trackingContext.Provider>
  );
};

export { useTracking, TrackingProvider, initializeAnalytics };
